/**
 * UploadProgress Component
 * Displays upload progress bar with file count
 */

import { html, attr, Signal, computedOf } from '@tempots/dom'

// ============================================================================
// Types
// ============================================================================

export interface UploadProgressProps {
  /** Current upload progress (0-100) */
  progress: Signal<number>
  /** Total number of files being uploaded */
  totalCount: Signal<number>
  /** Number of files already uploaded */
  uploadedCount: Signal<number>
}

// ============================================================================
// Component
// ============================================================================

/**
 * UploadProgress - Shows upload progress with file count
 *
 * @example
 * ```typescript
 * When(uploading, () =>
 *   UploadProgress({
 *     progress: uploadProgress,
 *     totalCount: uploadingCount,
 *     uploadedCount: uploadedCount,
 *   })
 * )
 * ```
 */
export function UploadProgress({
  progress,
  totalCount,
  uploadedCount,
}: UploadProgressProps) {
  return html.div(
    attr.class('flex items-center gap-2'),
    html.div(
      attr.class('w-32 h-2 bg-gray-200 rounded-full overflow-hidden'),
      html.div(
        attr.class('h-full bg-brand-600 transition-all'),
        attr.style(progress.map((p) => `width: ${p}%`))
      )
    ),
    html.span(
      attr.class('text-sm text-gray-600'),
      computedOf(
        totalCount,
        uploadedCount,
        progress
      )((total, uploaded, prog) => {
        if (total > 1) {
          return `${uploaded}/${total} files`
        }
        return `${prog}%`
      })
    )
  )
}
